import { DestructiveModal, ErrorModal, Flex, ListItem, Text, Toast } from '@modusign/design-system';
import { getGetLabelQueryKey, useRemoveLabel } from '@modusign/openapi-client-category';
import { useQueryClient } from 'react-query';

import {
  useInvalidateLabel,
  useInvalidateLabelList,
} from '@app/features/label/hooks/useInvalidateLabelList';
import useResourceLabel from '@app/features/label/hooks/useResourceLabel';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import josa from '@app/libs/josa';

interface LabelDeleteConfirmModalProps {
  id: string;
  onClose: () => void;
  onDeleted: () => void;
}

export default function LabelDeleteConfirmModal({
  id,
  onClose,
  onDeleted,
}: LabelDeleteConfirmModalProps) {
  const { data } = useResourceLabel(id);
  // 로딩 처리는 상위 컴포넌트에서 진행
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const label = data!;
  const queryClient = useQueryClient();

  const workspaceId = useWorkspaceId();
  const invalidateLabels = useInvalidateLabelList();
  const invalidateLabel = useInvalidateLabel();

  const { mutate, isLoading, reset, error } = useRemoveLabel<Error>({
    mutation: {
      onSuccess: () => {
        reset();
        Toast.success({
          description: '라벨을 삭제했습니다.',
        });
        invalidateLabels();
        invalidateLabel(id);
        queryClient.removeQueries(getGetLabelQueryKey(workspaceId, label.id));
        onDeleted();
      },
    },
  });

  return (
    <DestructiveModal
      closeButtonLabel="취소"
      confirmButtonLabel="삭제"
      isLoading={isLoading}
      isOpen
      maxWidth="xs"
      title="라벨 삭제"
      variant="destructive"
      onClose={onClose}
      onConfirm={() => mutate({ workspaceId, labelId: label.id })}
    >
      <Text fontWeight="medium" textAlign="center">{`'${label.name}'${josa.only(
        label.name,
        '을/를'
      )} 삭제할까요?`}</Text>
      <Text color="textNegativeBase" mt={1} textAlign="center">
        모든 데이터(문서, 계약 등)에서 이 라벨이 제거됩니다.
      </Text>
      <Flex
        as="ul"
        bg="boxBackgroundNeutralBase"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        flexDirection="column"
        gap={2}
        mt={7}
        px={5}
        py={7}
      >
        {[
          '예약된 문서는 이 라벨이 적용되지 않고 전송됩니다.',
          '라벨이 적용된 문서와 계약 등의 데이터는 삭제되지 않습니다',
          '모든 멤버가 이 라벨을 이용할 수 없게 됩니다.',
          '삭제된 라벨은 복구할 수 없습니다.',
          '템플릿에 적용된 라벨이 삭제됩니다.',
        ].map((str, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <ListItem key={idx} variant="square">
            <Text fontSize="sm">{str}</Text>
          </ListItem>
        ))}
      </Flex>
      <ErrorModal error={error} isOpen title="라벨 삭제 실패" onClose={reset} />
    </DestructiveModal>
  );
}
